/* Opening a record in another register.
 *
 * The registers are joined in the data — a person carries group names, a group
 * carries member ids, a device carries the id of whoever it is signed out to —
 * and until now none of that was joined in the UI. You could read that Simon is
 * in SG-IT-Admins and then had to go to Groups and search for it by hand to find
 * out who else is, which is the exact question the group register exists for.
 *
 * So a link carries `?open=`, and the target screen opens that record once its
 * rows have loaded. The value is whatever the source had to hand: an id where
 * one is stored, a name where it is not — a person's Groups column holds display
 * names, not ids, so matching has to allow both.
 *
 * A side effect worth having: every record now has a URL. `/groups?open=SG-IT-
 * Admins` is a link somebody can send.
 */

import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

export function useOpenRecord<T>(
  rows: readonly T[],
  matches: (row: T, value: string) => boolean,
  open: (row: T) => void,
): void {
  const [params] = useSearchParams();
  const value = params.get('open');

  useEffect(() => {
    /* Rows arrive asynchronously, so this runs again when they do rather than
       giving up on the first pass and leaving the panel shut. */
    if (!value || rows.length === 0) return;
    const found = rows.find((row) => matches(row, value));
    if (found) open(found);
    // `matches` and `open` are redefined every render by every caller; depending
    // on them would reopen the record on each keystroke in the search box.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, rows]);
}

/** The link a relationship points at. Encoded, because group names contain
    spaces and one on this tenant contains an '@'. */
export { recordHref } from '../lib/recordHref';
